import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import type { IntlKey } from 'locales';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { camelCaseToWords } from 'utils/string';

import { CheckBoxCell } from './CheckBoxCell';
import type { ProductCodeData } from './ProductCodeData';
import { TextFieldCell } from './TextFieldCell';

interface ProductCodesRowProps {
  code: string;
  displayInTermsheet: boolean;
  isDisplayInTermsheetReadonly: boolean;
  isDisplayInTermsheetVisible: boolean;
  isRequested: boolean;
  isRequestedReadonly: boolean;
  isValueReadonly: boolean;
  rowIndex: number;
  value: string;

  getCellHighlight: (rowIndex: number, columnId: string) => string | undefined;
  onCellChange: (key: string, column: keyof ProductCodeData, value: unknown) => void;
}

const StyledTableRow = styled.tr`
  border: solid 1px var(--bs-bg-lvl4);
`;

const StyledTableCellCode = styled.td`
  vertical-align: middle;
  font-weight: bold;
`;

export const ProductCodesRow: FC<ProductCodesRowProps> = ({
  code,

  onCellChange,
  isValueReadonly,
  isRequestedReadonly,
  isDisplayInTermsheetReadonly,
  isDisplayInTermsheetVisible,
  displayInTermsheet,
  isRequested,
  value,
  rowIndex,
  getCellHighlight,
}) => {
  const { formatMessage } = useIntl();

  const onChangeIsRequested = (val: boolean) => {
    onCellChange(code, 'ProductCodeIsRequested', val);
  };

  const onChangeDisplayTS = (val: boolean) => {
    onCellChange(code, 'ProductCodeDisplayInTS', val);
  };

  const onChangeCodeValue = (val: string) => {
    onCellChange(code, 'ProductCodeValue', val);
  };

  const labelCode = useMemo(
    () =>
      formatMessage({
        id: ('form.productCode.' + code) as IntlKey,
        defaultMessage: camelCaseToWords(code).toUpperCase(),
      }),
    [code, formatMessage],
  );
  return (
    <StyledTableRow data-e2e={'product-code-' + code}>
      <StyledTableCellCode data-e2e="product-code-key" className="p-3" id={`ProductCodes-ProductCodeKey-${code}`}>
        {labelCode}
      </StyledTableCellCode>
      <td data-e2e="product-code-value" className={classNames('pt-2', getCellHighlight(rowIndex, 'ProductCodeValue'))}>
        <TextFieldCell
          id={`ProductCodes-ProductCodeValue-${code}`}
          isReadOnly={isValueReadonly}
          onValueChange={onChangeCodeValue}
          value={value}
          placeholderKey={`form.productCode.placeholder.${code}` as IntlKey}
        />
      </td>
      <td data-e2e="product-code-is-requested" className={getCellHighlight(rowIndex, 'ProductCodeIsRequested')}>
        <CheckBoxCell
          id={`ProductCodes-ProductCodeIsRequested-${code}`}
          className="text-center"
          isReadOnly={isRequestedReadonly}
          isEdited={isDefined(getCellHighlight(rowIndex, 'ProductCodeIsRequested'))}
          onValueChange={onChangeIsRequested}
          value={isRequested}
        />
      </td>
      {isDisplayInTermsheetVisible && (
        <td
          data-e2e="product-code-display-in-termsheet"
          className={getCellHighlight(rowIndex, 'ProductCodeDisplayInTS')}
        >
          <CheckBoxCell
            id={`ProductCodes-ProductCodeDisplayInTS-${code}`}
            className="text-center"
            isReadOnly={isDisplayInTermsheetReadonly}
            isEdited={isDefined(getCellHighlight(rowIndex, 'ProductCodeDisplayInTS'))}
            onValueChange={onChangeDisplayTS}
            value={displayInTermsheet}
          />
        </td>
      )}
    </StyledTableRow>
  );
};

ProductCodesRow.displayName = 'ProductCodesRow';
